// PII data-minimization for member-site Segment events (OMG-1945).
//
// Pure scrub applied to every event via a source middleware on the member
// (New Member Home) Segment source — see segmentInstance.ts for where it's
// registered and why it's gated to the member source only. The checkout
// source and its revenue destinations are intentionally untouched.
//
// Two rules:
//   1. Hard-strip the auto-injected page context (url/path/title/referrer/
//      search). analytics-next populates these from window.location/document
//      on every call; they leak which member page/article the user is on.
//   2. Drop-by-default allowlist on `properties` (and `traits` for identify):
//      only known-good keys survive, so a future event or property can't
//      silently start leaking content identifiers (which video, which slug).
//
// `context` is a protected namespace — only the five page keys are deleted.
// context.fbc / context.fbp (Meta CAPI matching, OMG-1858) and SDK fields
// (messageId, anonymousId, library, integrations) are left intact.

import { AnalyticsEvent } from "@root/constants";

// A/B + environment keys shared by member properties and identify traits.
const ENGAGEMENT_KEYS = [
  "env",
  "experiment",
  "experiment_id",
  "variant",
  "variant_id",
] as const;

// Member-engagement properties the metrics depend on (OMG-1945 Key Metric
// Reporting). Anything not here is dropped. Single source of truth.
export const PROPERTY_ALLOWLIST = new Set<string>([
  ...ENGAGEMENT_KEYS,
  "scroll_depth",
  "threshold",
  "progress",
  "duration",
  "seek",
  // video_errored diagnostics (OMG-1908) — non-content scalars kept so 403-rate
  // dashboards keep working. Content identifiers (video_id, video_url,
  // error_message, error_details) are deliberately NOT listed: they're dropped
  // here per the PII goal and retained in Sentry `extra` for deep debugging.
  "error_type",
  "error_fatal",
  "is_403_error",
  "has_policy",
  "has_signature",
  "has_keypair",
  "all_present",
  "is_top_frame",
  "native_hls",
  "cookie_present_at_request",
  // OMG-2533: which loader failed (hls | media | play | stall) and the native
  // MediaError code, so native-iOS failures can be sliced apart from hls.js.
  "error_source",
  "media_error_code",
]);

// auth_error diagnostics. Scoped to event === "auth_error" only (NOT global):
// code/message/error are among the most common property names in any JS error
// object, so allowlisting them globally would let unrelated future events leak
// those fields. Sensitive fields (sessionData, checkoutKey) and route params
// stay dropped; full error context is retained in Sentry.
const AUTH_ERROR_ALLOWLIST = new Set<string>([
  ...PROPERTY_ALLOWLIST,
  "code",
  "message",
  "error",
]);

// Simulation diagnostics (OMG-2270), scoped to the four simulation events by
// exact name, not prefix, so a future simulation_* event with free-text fields
// has to opt in here. All kept values are literals at their call sites:
//   phase   exited:  loading | preparing | ready | started | error
//           errored: auth | poster | render | engine | load | decode |
//                    bootstrap | audio | audio_download | start
//   reason  dismissed | completed | replaced | retry | unmounted
//   surface poster | render | engine;  engine_event: StateError
//   started boolean;  locale: a supported i18n language
// Deliberately NOT listed: `simulation_id` and the engine's `sim` (both the
// package, i.e. which woman / technique page: a content identifier like
// video_id), `error` (raw error text, which can carry signed asset URLs) and
// the engine's state-machine payload. Sentry keeps them.
const SIMULATION_EVENTS = new Set<string>([
  AnalyticsEvent.SIMULATION_LOADED,
  AnalyticsEvent.SIMULATION_STARTED,
  AnalyticsEvent.SIMULATION_EXITED,
  AnalyticsEvent.SIMULATION_ERRORED,
]);
const SIMULATION_ALLOWLIST = new Set<string>([
  ...PROPERTY_ALLOWLIST,
  "phase",
  "started",
  "reason",
  "surface",
  "engine_event",
  "locale",
]);

// Lean-back mode (OMG-2753, OMG-2747), scoped to the four leanback_* events
// by exact name. Every kept value is a small integer or one of a few fixed
// words at its call site:
//   video_count, from_index, to_index, index, watched_count: integers
//   entry    button | url
//   advance  auto | manual
//   reason   ended | back | unmount
// Deliberately NOT listed: the technique page id and the video id (content
// identifiers, same rule as video_tracked).
const LEANBACK_EVENTS = new Set<string>([
  AnalyticsEvent.LEANBACK_STARTED,
  AnalyticsEvent.LEANBACK_ADVANCED,
  AnalyticsEvent.LEANBACK_COMPLETED,
  AnalyticsEvent.LEANBACK_EXITED,
]);
const LEANBACK_ALLOWLIST = new Set<string>([
  ...PROPERTY_ALLOWLIST,
  "video_count",
  "entry",
  "from_index",
  "to_index",
  "advance",
  "index",
  "watched_count",
  "reason",
]);

const allowlistFor = (event: unknown): Set<string> => {
  if (event === "auth_error") return AUTH_ERROR_ALLOWLIST;
  if (typeof event === "string" && SIMULATION_EVENTS.has(event)) {
    return SIMULATION_ALLOWLIST;
  }
  if (typeof event === "string" && LEANBACK_EVENTS.has(event)) {
    return LEANBACK_ALLOWLIST;
  }
  return PROPERTY_ALLOWLIST;
};

// identify traits. email is retained as the HDYHAU / Meta-CAPI join key
// (confirmed in-scope); entitlements and locale drive segmentation.
// member_home_arm is the Collections Home A/B test arm (OMG-2581): the
// BigQuery readout joins first-week engagement to it via
// new_member_home.identifies, so it must survive the scrub. Value is one of
// two fixed words, never a content identifier.
export const TRAIT_ALLOWLIST = new Set<string>([
  "email",
  "entitlements",
  "locale",
  "member_home_arm",
  ...ENGAGEMENT_KEYS,
]);

const PAGE_PII_KEYS = ["url", "path", "title", "referrer", "search"] as const;

type RawEvent = Record<string, any>;

const pick = (source: RawEvent, allow: Set<string>): RawEvent => {
  const out: RawEvent = {};
  for (const key of Object.keys(source)) {
    if (allow.has(key)) out[key] = source[key];
  }
  return out;
};

// Mutates and returns the raw Segment event (`payload.obj`). The facade hands
// us a clone, so in-place mutation is the supported, expected idiom.
export const scrubEvent = (obj: RawEvent): RawEvent => {
  if (!obj || typeof obj !== "object") return obj;

  // 1. Strip auto-injected page context PII.
  const page = obj.context?.page;
  if (page && typeof page === "object") {
    for (const key of PAGE_PII_KEYS) delete page[key];
  }

  // 2. Allowlist properties (covers track and page; for page() the SDK also
  // mirrors page fields into properties, which the allowlist drops).
  // auth_error and simulation_* events get their extra diagnostic keys; all
  // others use the base set.
  if (obj.properties && typeof obj.properties === "object") {
    obj.properties = pick(obj.properties, allowlistFor(obj.event));
  }

  // 3. Allowlist identify traits.
  if (obj.type === "identify" && obj.traits && typeof obj.traits === "object") {
    obj.traits = pick(obj.traits, TRAIT_ALLOWLIST);
  }

  return obj;
};
